import { motion } from 'framer-motion';
import { Award, ExternalLink } from 'lucide-react';

// LinkedIn's official "Licenses & certifications" details page — safe verified fallback
const LI_CERTS = 'https://www.linkedin.com/in/anshuu/details/certifications/';

// Top 5 credentials only — the rest live on LinkedIn to keep the landing crisp.
const certifications = [
    {
        title: 'AI Foundations & Design Thinking',
        issuer: 'AI CERTs®',
        date: 'May 2026',
        credentialId: 'OH2OHAWTDWQD',
        url: 'https://www.coursera.org/account/accomplishments/verify/OH2OHAWTDWQD',
    },
    {
        title: 'Fundamentals of UI/UX Design',
        issuer: 'Microsoft',
        date: 'Dec 2024',
        credentialId: 'MGVON0HKVJIY',
        skills: ['User Experience (UX)', 'Prototyping', '+3'],
        featured: true,
        url: 'https://www.coursera.org/account/accomplishments/verify/MGVON0HKVJIY',
    },
    {
        title: 'Introduction to User Experience Design',
        issuer: 'Georgia Institute of Technology',
        date: 'Dec 2024',
        credentialId: 'A6U7LMO3WFN1',
        skills: ['User Experience (UX)', 'Prototyping'],
        url: 'https://www.coursera.org/account/accomplishments/verify/A6U7LMO3WFN1',
    },
    {
        title: 'Design Patterns for AI UX',
        issuer: 'IxDF · Interaction Design Foundation',
        date: 'Jun 2024',
        url: 'https://ixdf.org/members/himanshu-kumar-tanty/certificate/masterclass/mcc_e16559285670480f9f6e4709ca38d8af',
    },
    {
        title: 'Foundations of User Experience (UX) Design',
        issuer: 'Coursera',
        date: 'Dec 2023',
        credentialId: '5CS7T65K362L',
        skills: ['UX Research', 'UED', '+1'],
        url: 'https://www.coursera.org/account/accomplishments/certificate/5CS7T65K362L',
    },
];

const Card = ({ c, i }) => (
    <motion.a
        href={c.url || LI_CERTS}
        target="_blank"
        rel="noreferrer"
        data-testid={`cert-card-${i}`}
        data-cursor="hover"
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-6%' }}
        transition={{ duration: 0.6, delay: i * 0.04 }}
        className={`group relative flex flex-col justify-between gap-4 p-5 rounded-2xl border transition-all duration-500 min-h-[160px] ${
            c.featured
                ? 'bg-[var(--accent)] text-[var(--accent-ink)] border-[var(--accent)] hover:opacity-95'
                : 'bg-[var(--surface)] border-[var(--line)] hover:border-[var(--line-2)]'
        }`}
    >
        <div className="flex items-start justify-between gap-3">
            <span
                className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${
                    c.featured
                        ? 'bg-[var(--accent-ink)] text-[var(--accent)]'
                        : 'bg-[var(--surface-2)] text-[var(--accent)] border border-[var(--line-2)]'
                }`}
            >
                <Award size={14} />
            </span>
            <ExternalLink
                size={13}
                className={
                    c.featured
                        ? 'text-[var(--accent-ink)] opacity-60 group-hover:opacity-100 transition-opacity'
                        : 'text-[var(--ink-3)] group-hover:text-[var(--accent)] transition-colors'
                }
            />
        </div>

        <div>
            <h3
                className={`font-display-italic text-lg md:text-xl leading-[1.15] ${
                    c.featured ? 'text-[var(--accent-ink)]' : 'text-[var(--ink)]'
                }`}
            >
                {c.title}
            </h3>
            <div
                className={`mt-2 font-mono-brand text-[10px] uppercase tracking-[0.18em] ${
                    c.featured
                        ? 'text-[var(--accent-ink)] opacity-70'
                        : 'text-[var(--ink-3)]'
                }`}
            >
                {c.issuer} · {c.date}
            </div>
        </div>
    </motion.a>
);

const Credentials = () => {
    return (
        <section
            id="credentials"
            data-testid="credentials-section"
            className="relative px-4 md:px-6 py-10 md:py-12"
        >
            <div className="mx-auto max-w-[1400px]">
                {/* Section head */}
                <div className="flex items-end justify-between gap-6 flex-wrap mb-8 md:mb-10">
                    <div>
                        <span className="chip mb-3 inline-flex">
                            <Award size={11} className="text-[var(--accent)]" />
                            Credentials
                        </span>
                        <h2 className="font-display leading-[0.95] text-4xl md:text-6xl text-[var(--ink)]">
                            Licenses &
                            <span className="font-display-italic text-[var(--accent)]">
                                {' '}certifications
                            </span>
                            .
                        </h2>
                    </div>
                    <a
                        href={LI_CERTS}
                        target="_blank"
                        rel="noreferrer"
                        data-cursor="hover"
                        className="link-underline font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink)] whitespace-nowrap"
                    >
                        See all 8+ credentials on LinkedIn ↗
                    </a>
                </div>

                {/* Grid — 5 cols on desktop, 2 on tablet, 1 on mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
                    {certifications.map((c, i) => (
                        <Card key={c.title} c={c} i={i} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Credentials;
